import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import _ from 'lodash';
import { ShortcutTypeGlobal } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeGlobal';
import { SettingsState } from '../../settings/settings.state';
import { CloseHotkeyDuplicationDialog, OpenHotkeyDuplicationDialog } from './shortcut-behaviour.actions';

export interface ShortcutBehaviourStateModel {
   hotkeys: ShortcutTypeGlobal[];
   hotkeyDuplicateDialogOpen: { [key: string]: boolean };
}

@State<ShortcutBehaviourStateModel>({
   name: 'shortcutBehaviourState',
   defaults: {
      hotkeys: _(ShortcutTypeGlobal.values()).value(),
      hotkeyDuplicateDialogOpen: {},
   },
})
@Injectable()
export class ShortcutBehaviourState {
   @Selector([ShortcutBehaviourState])
   public static getHotkeys(state: ShortcutBehaviourStateModel) {
      return state.hotkeys;
   }

   @Selector([ShortcutBehaviourState.getHotkeys])
   public static globalShortCutList(shortCutList: ShortcutTypeGlobal[]) {
      return shortCutList.map((sc) => sc.value);
   }

   @Selector([SettingsState.getHotkeySettings])
   public static settingShortCutList(hotkeySettings: { [key: string]: string }) {
      return _(hotkeySettings)
         .map((value, key) => key)
         .value();
   }

   @Selector([ShortcutBehaviourState.globalShortCutList, ShortcutBehaviourState.settingShortCutList])
   public static shortCutDifferences(scGlobal: string[], scSettings: string[]) {
      return _.difference(scGlobal, scSettings);
   }

   @Selector([ShortcutBehaviourState.shortCutDifferences, ShortcutBehaviourState.settingShortCutList])
   public static areShortCutListsEqual(scDifferences: string[]) {
      return _.isEmpty(scDifferences);
   }

   @Selector([ShortcutBehaviourState.getHotkeys, SettingsState.getHotkeySettings])
   public static getSettingsToUpdate(
      shortCutList: ShortcutTypeGlobal[],
      hotkeySettings: {
         [key: string]: string;
      }
   ) {
      const updateSettings: { [key: string]: string } = {};
      shortCutList.forEach((sc) => {
         if (_.isNil(hotkeySettings[sc.value])) {
            updateSettings[sc.value] = sc?.defaultHotkeyCombo;
            return;
         }
         updateSettings[sc.value] = hotkeySettings[sc.value];
      });
      return updateSettings;
   }

   @Selector([ShortcutBehaviourState.getHotkeys, SettingsState.getHotkeySettingForHotkeyType])
   public static getHotkeysWithValue(shortCutList: ShortcutTypeGlobal[], getHotkeySetting: (sctype) => string) {
      return _(shortCutList)
         .map((shortCut) => {
            const combo = getHotkeySetting(shortCut);
            const hotkey = _.isString(combo) && combo.length > 0 ? combo : '';
            return hotkey;
         })
         .value();
   }

   @Selector([ShortcutBehaviourState.getHotkeys, SettingsState.getHotkeySettingForHotkeyType])
   public static getHotkeysWithShortCutCombo(shortCutList: ShortcutTypeGlobal[], getHotkeySetting: (sctype) => string) {
      return (shortcutCombo: string) => {
         return _(shortCutList)
            .filter((shortCut) => {
               const combo = getHotkeySetting(shortCut);
               const hotkey = _.isString(combo) && combo.length > 0 ? combo : '';
               return hotkey === shortcutCombo;
            })
            .value();
      };
   }

   @Selector([ShortcutBehaviourState])
   public static getHotkeyDialog(state: ShortcutBehaviourStateModel): Record<string, boolean> {
      return state.hotkeyDuplicateDialogOpen;
   }

   @Selector([ShortcutBehaviourState.getHotkeyDialog])
   public static getIsHotkeyDuplicateDialogOpen(hotkeyDialog: Record<string, boolean>) {
      return (hotkeyTyp: ShortcutTypeGlobal) => {
         return hotkeyDialog[hotkeyTyp.value];
      };
   }

   @Action(OpenHotkeyDuplicationDialog)
   public openHotkeyDuplicationDialog(ctx: StateContext<ShortcutBehaviourStateModel>, action: OpenHotkeyDuplicationDialog) {
      const isDialogOpen: { [key: string]: boolean } = {};
      isDialogOpen[action.shortcutType.value] = true;
      ctx.patchState({
         hotkeyDuplicateDialogOpen: isDialogOpen,
      });
   }

   @Action(CloseHotkeyDuplicationDialog)
   public closeHotkeyDuplicationDialog(ctx: StateContext<ShortcutBehaviourStateModel>, action: CloseHotkeyDuplicationDialog) {
      const isDialogOpen: { [key: string]: boolean } = {};
      isDialogOpen[action.shortcutType.value] = false;
      ctx.patchState({
         hotkeyDuplicateDialogOpen: isDialogOpen,
      });
   }
}
